import type { AutoAdvanceHold } from '../types'

export const AUTO_ADVANCE_MIN_MS = 3000
export const AUTO_ADVANCE_MAX_MS = 12_000
const BASE_MS = 2500
/** ≈240 wpm: 1 s per 4 words. */
const PER_WORD_MS = 250

/** How long a card stays up before auto-advancing: 2.5 s + 1 s per ~4 words of the shown text, clamped 3–12 s. */
export function autoAdvanceMs(plainText: string): number {
  const words = plainText.split(/\s+/).filter(Boolean).length
  return Math.min(AUTO_ADVANCE_MAX_MS, Math.max(AUTO_ADVANCE_MIN_MS, BASE_MS + words * PER_WORD_MS))
}

/**
 * One countdown, framework-free. It runs only while armed AND nothing holds it
 * (hover, focus, a hidden tab, an explicit Pause, a redraw in progress); a hold
 * freezes the remaining time and releasing every hold continues from there.
 * Re-arming with the SAME key (a redraw of the same content) continues; a new
 * key (another step, or the card's copy changed) restarts from full. `clear()`
 * drops everything — no timeout survives it.
 */
export class AutoAdvanceTimer {
  private timer: ReturnType<typeof setTimeout> | undefined
  private armedKey: string | null = null
  private duration = 0
  private left = 0
  private startedAt = 0
  private holds = new Set<AutoAdvanceHold>()
  private readonly onElapse: (key: string) => void
  private readonly onChange: () => void

  constructor(onElapse: (key: string) => void, onChange: () => void = () => undefined) {
    this.onElapse = onElapse
    this.onChange = onChange
  }

  get key(): string | null {
    return this.armedKey
  }

  get running(): boolean {
    return this.timer !== undefined
  }

  get durationMs(): number {
    return this.duration
  }

  remaining(now = Date.now()): number {
    return this.running ? Math.max(0, this.left - (now - this.startedAt)) : this.left
  }

  isHeld(reason: AutoAdvanceHold): boolean {
    return this.holds.has(reason)
  }

  /** Start (or, same key, continue) the countdown; `holds` replaces the current set. */
  arm(key: string, ms: number, holds: AutoAdvanceHold[] = []): void {
    this.stopClock()
    if (key !== this.armedKey) {
      this.duration = ms
      this.left = ms
    }
    this.armedKey = key
    this.holds = new Set(holds)
    this.sync()
  }

  hold(reason: AutoAdvanceHold, on: boolean): void {
    if (this.armedKey === null || this.holds.has(reason) === on) return
    if (on) this.holds.add(reason)
    else this.holds.delete(reason)
    this.sync()
  }

  clear(): void {
    const had = this.armedKey !== null
    this.stopClock()
    this.armedKey = null
    this.holds.clear()
    this.duration = 0
    this.left = 0
    if (had) this.onChange()
  }

  private stopClock(): void {
    if (!this.running) return
    this.left = this.remaining()
    clearTimeout(this.timer)
    this.timer = undefined
  }

  private sync(): void {
    if (this.armedKey !== null && this.holds.size === 0) {
      if (!this.running) {
        const key = this.armedKey
        this.startedAt = Date.now()
        this.timer = setTimeout(() => {
          this.timer = undefined
          this.clear()
          this.onElapse(key)
        }, this.left)
      }
    } else {
      this.stopClock()
    }
    this.onChange()
  }
}
